import styles from "./EmployeeWizard.module.scss";
import { FormProvider } from "react-hook-form";
import {
  IEmployeeWizardFormModel,
  IEmployeeStudioFormModel,
} from "./models/employeeWizardFormModel";
import { nameof } from "ts-simple-nameof";
import { useEmployeeWizardPage } from "./hooks/useEmployeeWizardPage";
import { SingleSectionPageLayout } from "../../../../common/components/common/SingleSectionPageLayout";
import { DefaultButton } from "../../../../common/components/button/defaultButton/DefaultButton";
import { DefaultInputField } from "../../../../common/components/forms/inputs/InputField/DefaultInputField";
import { PageMode } from "../../../../common/types";
import { RenderWhen } from "../../../../common/infrastructure/components/RenderWhen";
import { HiddenField } from "../../../../common/components/forms/inputs/hiddenField/HiddenField";
import { DefaultPhoneInputField } from "../../../../common/components/forms/inputs/phoneField/DefaultPhoneInputField";
import { DefaultFormSelectField } from "../../../../common/components/forms/inputs/dropdown/DefaultFormSelectField";
import { AppColorPicker } from "../../../../common/components/forms/inputs/colorPicker/AppColorPicker";
import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { classNames } from "primereact/utils";
import { PortalTooltip } from "../../../../common/components/tooltip/PortalTooltip";
import { useSafeId } from "../../../../common/components/tooltip/hooks/useSafeId";
import tooltipStyles from "../../../../common/components/tooltip/AppTooltip.module.scss";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import { WorkingHoursDialog } from "../../../../common/components/common/workingHoursDialog/WorkingHoursDialog";
import { DefaultToggle } from "../../../../common/components/forms/inputs/toggle/DefaultToggle";
import {
  AppAccordion,
  IAppAccordionTab,
} from "../../../../common/components/AppAccordion/AppAccordion";
import { MultiSelectField } from "../../../../common/components/forms/inputs/dropdown/MultiSelectSelectField";
import { SectionDivider } from "../../../../common/components/common/SectionDivider/SectionDivider";
import { PaymentTypes, StudioType } from "../../../../common/enums";
import { employeeRolesTooltip } from "../../../../common/utilities/roleUtility";

interface IEmployeeWizardProps {
  pageMode: PageMode;
}

export const EmployeeWizard = ({ pageMode }: IEmployeeWizardProps) => {
  const {
    id,
    form,
    pageTitle,
    actionButtonLabel,
    employeeRoles,
    disableRoleDropdown,
    isStudioSelectionDisabled,
    areSignInDetailsLocked,
    availableStudios,
    workingHoursData,
    showServicesTypeOptions,
    showLockOthersEventsToggle,
    handleSubmit,
  } = useEmployeeWizardPage(pageMode);

  const [showWorkingTimeDialog, setShowWorkingTimeDialog] = useState(false);
  const [showTooltipSheet, setShowTooltipSheet] = useState(false);
  const [isTooltipSheetClosing, setIsTooltipSheetClosing] = useState(false);
  const workingTimeBtnId = useSafeId();

  const isAddMode = pageMode === "add";
  const workingTimeTooltipText =
    "След като създадете служителя, можете да го редактирате и да зададете работното му време.";

  const openTooltipSheet = () => {
    setIsTooltipSheetClosing(false);
    setShowTooltipSheet(true);
  };

  const closeTooltipSheet = () => {
    setIsTooltipSheetClosing(true);
    setTimeout(() => {
      setShowTooltipSheet(false);
      setIsTooltipSheetClosing(false);
    }, 280);
  };

  const studios = form.watch("studios") || [];
  const availableStudioIds = useMemo(
    () => new Set(availableStudios.map((s) => s.id)),
    [availableStudios],
  );

  // Only show studios that still exist (filter out deleted ones)
  const activeStudios = useMemo(
    () => studios.filter((s) => availableStudioIds.has(s.studioId)),
    [studios, availableStudioIds],
  );

  const selectedStudioIds = useMemo(
    () => activeStudios.map((s) => s.studioId),
    [activeStudios],
  );

  const canPickStudios = availableStudios.length > 1;

  const handleStudioSelectionChange = (newSelectedIds: number[] | null) => {
    const ids = newSelectedIds || [];
    const currentStudios = form.getValues("studios") || [];

    // Build new studios array preserving existing commission data
    const newStudios = ids.map((studioId): IEmployeeStudioFormModel => {
      const existing = currentStudios.find((s) => s.studioId === studioId);
      if (existing) {
        return existing as IEmployeeStudioFormModel;
      }
      const studioInfo = availableStudios.find((s) => s.id === studioId);
      return {
        studioId,
        studioName: studioInfo?.name || "",
        commissionPercent: null,
        weekendCommissionPercent: null,
        individualCommissionPercent: null,
        individualWeekendCommissionPercent: null,
        multisportCommissionPercent: null,
        multisportWeekendCommissionPercent: null,
        coolFitCommissionPercent: null,
        coolFitWeekendCommissionPercent: null,
        serviceCommissionPercent: null,
        serviceWeekendCommissionPercent: null,
        planCommissionPercent: null,
        planWeekendCommissionPercent: null,
      };
    });

    form.setValue("studios", newStudios, { shouldValidate: true });
  };

  // A single "Проценти" subsection: a divider heading plus weekday/weekend fields.
  const renderPercentPair = (
    formIndex: number,
    label: string,
    weekdayName: string,
    weekendName: string,
  ) => (
    <>
      <SectionDivider label={label} width="100%" />
      <div className={styles.root_studio_commission}>
        <DefaultInputField
          name={`studios.${formIndex}.${weekdayName}`}
          label="Делник"
          placeholder="50%"
          type="number"
          step={0.01}
          width="100%"
        />
        <DefaultInputField
          name={`studios.${formIndex}.${weekendName}`}
          label="Уикенд"
          placeholder="50%"
          type="number"
          step={0.01}
          width="100%"
        />
      </div>
    </>
  );

  // A studio that does not use commissions has nothing to set here, so it gets no tab.
  // Its stored percentages stay in the form and are saved back unchanged.
  const accordionTabs: IAppAccordionTab[] = useMemo(
    () =>
      activeStudios
        .filter(
          (studio) =>
            availableStudios.find((s) => s.id === studio.studioId)
              ?.usesCommissions !== false,
        )
        .map((studio) => {
          // Find the actual index in the full studios array for correct form field binding
          const formIndex = studios.findIndex(
            (s) => s.studioId === studio.studioId,
          );

          const studioInfo = availableStudios.find(
            (s) => s.id === studio.studioId,
          );
          const types = studioInfo?.types ?? [];
          const payments = studioInfo?.supportedPaymentTypes ?? [];

          const supportsGroup = types.includes(StudioType.Group);
          const supportsService = types.includes(StudioType.Service);
          const supportsMultisport = payments.includes(PaymentTypes.Multisport);
          const supportsCoolFit = payments.includes(PaymentTypes.CoolFit);
          const supportsPlan =
            payments.includes(PaymentTypes.Plan) &&
            studioInfo?.usesPlans !== false;

          return {
            id: studio.studioId,
            header: studio.studioName,
            content: (
              <AppAccordion
                variant="dark"
                tabs={[
                  {
                    id: `percents-${studio.studioId}`,
                    header: "Комисионни проценти",
                    content: (
                      <>
                        <RenderWhen condition={supportsGroup}>
                          {renderPercentPair(
                            formIndex,
                            "Комисионен процент за групови класове",
                            "commissionPercent",
                            "weekendCommissionPercent",
                          )}
                          {renderPercentPair(
                            formIndex,
                            "Комисионен процент за индивидуални класове",
                            "individualCommissionPercent",
                            "individualWeekendCommissionPercent",
                          )}
                        </RenderWhen>

                        <RenderWhen condition={supportsMultisport}>
                          {renderPercentPair(
                            formIndex,
                            "Комисионен процент за MultiSport",
                            "multisportCommissionPercent",
                            "multisportWeekendCommissionPercent",
                          )}
                        </RenderWhen>

                        <RenderWhen condition={supportsCoolFit}>
                          {renderPercentPair(
                            formIndex,
                            "Комисионен процент за CoolFit",
                            "coolFitCommissionPercent",
                            "coolFitWeekendCommissionPercent",
                          )}
                        </RenderWhen>

                        <RenderWhen condition={supportsService}>
                          {renderPercentPair(
                            formIndex,
                            "Комисионен процент за услуги",
                            "serviceCommissionPercent",
                            "serviceWeekendCommissionPercent",
                          )}
                        </RenderWhen>

                        <RenderWhen condition={supportsPlan}>
                          {renderPercentPair(
                            formIndex,
                            "Комисионен процент за абонаментни планове",
                            "planCommissionPercent",
                            "planWeekendCommissionPercent",
                          )}
                        </RenderWhen>
                      </>
                    ),
                  },
                ]}
              />
            ),
          };
        }),
    [activeStudios, studios, availableStudios],
  );

  const children = (
    <>
      <div className={styles.root_fields}>
        <HiddenField name={nameof<IEmployeeWizardFormModel>((x) => x.id)} />

        <DefaultInputField
          name={nameof<IEmployeeWizardFormModel>((x) => x.firstName)}
          label="Име"
          placeholder="Ирина"
          required
        />

        <DefaultInputField
          name={nameof<IEmployeeWizardFormModel>((x) => x.lastName)}
          label="Фамилия"
          placeholder="Димитрова"
          required
        />

        <DefaultPhoneInputField
          label="Телефонен номер"
          name={nameof<IEmployeeWizardFormModel>((x) => x.phoneNumber)}
          disabled={areSignInDetailsLocked}
          required
        />

        <DefaultInputField
          name={nameof<IEmployeeWizardFormModel>((x) => x.email)}
          label="Имейл"
          placeholder="name@example.com"
          disabled={areSignInDetailsLocked}
          required
        />

        <DefaultFormSelectField
          name={nameof<IEmployeeWizardFormModel>((x) => x.role)}
          label="Роля"
          disabled={!!disableRoleDropdown}
          options={employeeRoles}
          includeId={false}
          tooltip={employeeRolesTooltip}
        />

        <AppColorPicker
          name={nameof<IEmployeeWizardFormModel>((x) => x.color)}
          label="Цвят"
        />

        <DefaultToggle
          name={nameof<IEmployeeWizardFormModel>((x) => x.viewOnlyOwnEvents)}
          label="Потребителят трябва да вижда само своите класове"
          tooltip="Когато е включено, служителят вижда в календара само класовете, на които той е инструкторът, както и класовете без назначен инструктор. Когато е изключено, вижда всички класове."
        />

        <RenderWhen condition={showLockOthersEventsToggle}>
          <DefaultToggle
            name={nameof<IEmployeeWizardFormModel>((x) => x.lockOthersEvents)}
            label="Потребителят трябва да не може да отваря класовете на другите инструктори"
            tooltip="Когато е включено, служителят вижда всички класове в календара, но класовете на другите инструктори са със заключено катинарче и не се отварят, редактират или изтриват. Неговите класове и класовете без назначен инструктор се отварят нормално."
          />
        </RenderWhen>
      </div>

      {/* With a single studio there is nothing to pick, so the picker goes; the section
          stays only for that studio's commission percents. */}
      <RenderWhen condition={canPickStudios || accordionTabs.length > 0}>
        <SectionDivider label="Обекти" />

        <div className={styles.root_studios_section}>
          <RenderWhen condition={canPickStudios}>
            <MultiSelectField
              value={selectedStudioIds}
              options={availableStudios}
              onChange={handleStudioSelectionChange}
              disabled={isStudioSelectionDisabled}
              label="Обекти"
              includeId={false}
              filter={false}
              width="100%"
            />
          </RenderWhen>

          <RenderWhen condition={accordionTabs.length > 0}>
            <AppAccordion
              tabs={accordionTabs}
              multiple
              defaultActiveIndex={[0]}
              variant="dark"
            />
          </RenderWhen>
        </div>
      </RenderWhen>

      <div className={styles.root_buttons}>
        <RenderWhen condition={showServicesTypeOptions}>
          <DefaultButton
            label={"Работно време"}
            design="outline"
            className={classNames(workingTimeBtnId, {
              [styles.root_disabled_button]: isAddMode,
            })}
            onClick={() => {
              if (isAddMode) {
                openTooltipSheet();
                return;
              }
              setShowWorkingTimeDialog(true);
            }}
          />

          {isAddMode && (
            <>
              <PortalTooltip
                anchorSelect={`.${workingTimeBtnId}`}
                place="top"
                className={tooltipStyles.root_tooltip}
                positionStrategy="fixed"
              >
                {workingTimeTooltipText}
              </PortalTooltip>

              {showTooltipSheet &&
                createPortal(
                  <div
                    className={classNames(tooltipStyles.overlay, {
                      [tooltipStyles.overlay_closing]: isTooltipSheetClosing,
                    })}
                    role="dialog"
                    aria-modal="true"
                    onClick={closeTooltipSheet}
                  >
                    <div
                      className={classNames(tooltipStyles.sheet, {
                        [tooltipStyles.sheet_closing]: isTooltipSheetClosing,
                      })}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className={tooltipStyles.sheet_header}>
                        <div className={tooltipStyles.sheet_titleRow} />
                        <button
                          type="button"
                          className={tooltipStyles.sheet_closeBtn}
                          onClick={closeTooltipSheet}
                        >
                          <i className="pi pi-times" />
                        </button>
                      </div>
                      <div className={tooltipStyles.sheet_content}>
                        {workingTimeTooltipText}
                      </div>
                    </div>
                  </div>,
                  document.body,
                )}
            </>
          )}
        </RenderWhen>

        <DefaultButton label={actionButtonLabel} type="submit" />

        <RenderWhen condition={pageMode === "add"}>
          <div className={styles.root_buttons_footer_text}>
            При добавяне на служителя, той ще получи линк за задаване на парола
            на имейла си.
          </div>
        </RenderWhen>
      </div>

      <WorkingHoursDialog
        visible={showWorkingTimeDialog}
        onHide={() => setShowWorkingTimeDialog(false)}
        submitEndpoint={endpointsMetadata.workingHours.saveWorkingHours}
        userId={Number(id) ?? 0}
        disclaimer="От тази информация зависи кои дати и часови диапазони за потребителя ще са деактивирани по подразбиране за нови резервации."
        workingHoursData={workingHoursData}
        invalidateKeys={[
          [
            endpointsMetadata.employee.getEmployee,
            endpointsMetadata.workingHours.getUserWorkingHours,
            endpointsMetadata.planner.wizardGetInitialData,
          ],
        ]}
      />
    </>
  );

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <SingleSectionPageLayout
          title={pageTitle}
          maxHeight={"90%"}
          children={children}
        />
      </form>
    </FormProvider>
  );
};
